import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDemoMode } from "../../contexts/DemoModeContext";
import { getTooltipForPath } from "./demoTooltipContent";
import "./DemoTooltip.css";

/**
 * DemoTooltip
 * Floating per-page guidance card.
 * - Auto-shows on first visit to a page (sessionStorage dismiss per route id)
 * - Can be re-opened via the floating "?" button
 * - Closable / ignorable at any time
 */
export default function DemoTooltip() {
  const { isDemoMode, demoCard } = useDemoMode();
  const location = useLocation();
  const [tooltip, setTooltip] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const match = getTooltipForPath(location.pathname, isDemoMode);
    setTooltip(match);

    if (match) {
      const key = `tooltipDismissed_${match.id}`;
      const alreadyDismissed = sessionStorage.getItem(key) === "true";
      setVisible(!alreadyDismissed);
    } else {
      setVisible(false);
    }
  }, [location.pathname, isDemoMode]);

  if (!tooltip) return null;

  function handleDismiss() {
    sessionStorage.setItem(`tooltipDismissed_${tooltip.id}`, "true");
    setVisible(false);
  }

  return (
    <>
      {/* Floating guide card */}
      {visible && (
        <div className="demo-tooltip" role="complementary" aria-label="Page guide">
          <div className="demo-tooltip__header">
            <span className="demo-tooltip__icon">Guide</span>
            <strong className="demo-tooltip__title">{tooltip.title}</strong>
            <button
              className="demo-tooltip__close"
              onClick={handleDismiss}
              aria-label="Dismiss guide"
            >
              ×
            </button>
          </div>
          <p className="demo-tooltip__body">{tooltip.body}</p>
          {tooltip.showCard && demoCard && (
            <div className="demo-tooltip__card">
              <div className="demo-tooltip__card-label">Test card</div>
              <div className="demo-tooltip__card-row">
                <code>{demoCard.number}</code>
                <code>Exp {demoCard.expiry}</code>
                <code>CVV {demoCard.cvv}</code>
                <code>ZIP {demoCard.zip}</code>
              </div>
            </div>
          )}
          <button className="demo-tooltip__got-it" onClick={handleDismiss}>
            Got it
          </button>
        </div>
      )}

      {/* Persistent "?" re-open button */}
      {!visible && (
        <button
          className="demo-tooltip__reopen"
          onClick={() => setVisible(true)}
          aria-label="Show page guide"
          title="Show guide"
        >
          ?
        </button>
      )}
    </>
  );
}
